// frontend/src/pages/PrivacyPolicyPage.jsx

import React from 'react';
import { Box, Container, Typography, Paper, Divider, Button, Link, Chip, Grid } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SecurityIcon from '@mui/icons-material/Security';
import PolicyIcon from '@mui/icons-material/Policy';
import CodeIcon from '@mui/icons-material/Code';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #030D1B 0%, #071933 50%, #020914 100%)',
        py: { xs: 4, md: 6 },
        px: 2,
        color: '#FFFFFF',
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            mb: 3,
            color: '#3897F0',
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': { backgroundColor: 'rgba(56, 151, 240, 0.1)' },
          }}
        >
          Voltar
        </Button>

        <Paper
          elevation={12}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 4,
            backgroundColor: 'rgba(7, 25, 51, 0.94)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.14)',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.65)',
            color: '#E0E6ED',
          }}
        >
          {/* Header Institucional */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <SecurityIcon sx={{ fontSize: 44, color: '#3897F0' }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#FFFFFF', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                Política de Privacidade e Termos de Uso
              </Typography>
              <Typography variant="subtitle1" sx={{ color: '#90CAF9', fontWeight: 600 }}>
                ITAPOÁ SIGN - Plataforma Institucional de Assinatura Digital
              </Typography>
            </Box>
          </Box>

          {/* Badges de Enquadramento Legal */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            <Chip label="Resolução nº XX/2026 - C.M.I." size="small" sx={{ backgroundColor: 'rgba(56, 151, 240, 0.2)', color: '#90CAF9', fontWeight: 600 }} />
            <Chip label="LGPD - Lei 13.709/2018" size="small" sx={{ backgroundColor: 'rgba(76, 175, 80, 0.2)', color: '#A5D6A7' }} />
            <Chip label="Lei Federal 14.063/2020" size="small" sx={{ backgroundColor: 'rgba(255, 152, 0, 0.2)', color: '#FFCC80' }} />
            <Chip label="Lei Federal 14.129/2021" size="small" sx={{ backgroundColor: 'rgba(156, 39, 176, 0.2)', color: '#CE93D8' }} />
            <Chip label="Lei Municipal 1.517/2026 (SMI)" size="small" sx={{ backgroundColor: 'rgba(0, 188, 212, 0.2)', color: '#80DEEA' }} />
          </Box>

          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.65)', mb: 3 }}>
            <strong>Origem:</strong> Poder Legislativo Municipal • Câmara Municipal de Itapoá - SC<br />
            <strong>Última atualização:</strong> 15 de outubro de 2026
          </Typography>

          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.15)', mb: 4 }} />

          {/* 1. Natureza Jurídica, Origem e Titularidade Institucional */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <AccountBalanceIcon sx={{ color: '#3897F0' }} />
              <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                1. Natureza Jurídica, Origem e Titularidade Institucional
              </Typography>
            </Box>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              O <strong>ITAPOÁ SIGN</strong> (formalmente instituído como <em>"Sign Itapoá"</em> pelo art. 8º da Resolução nº XX/2026 da Câmara Municipal de Itapoá) é uma solução tecnológica institucional pública, aberta e gratuita, concebida e mantida pelo <strong>Núcleo de Inovação Legislativa de Itapoá (NILI)</strong> no âmbito da Política de Inovação Tecnológica, Gestão do Conhecimento e Modernização Digital do Poder Legislativo.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Nos termos do art. 6º da referida Resolução e do art. 4º da Lei Federal nº 9.609/1998, todos os direitos patrimoniais, marcas, identidades visuais, rotinas e códigos-fonte pertencem com exclusividade à <strong>Câmara Municipal de Itapoá - SC</strong>.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              O sistema integra as iniciativas de desmaterialização de processos físicos, sustentabilidade institucional e integração ao <strong>Sistema Municipal de Inovação (SMI)</strong>, estabelecido pela Lei Municipal nº 1.517/2026.
            </Typography>
          </Box>

          {/* 2. Filosofia de Software Livre, Transparência e Código Aberto */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <CodeIcon sx={{ color: '#3897F0' }} />
              <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                2. Software Livre, Auditabilidade e Dados Abertos
              </Typography>
            </Box>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Em estrita obediência ao art. 7º da Resolução nº XX/2026, o ITAPOÁ SIGN é desenvolvido sob a filosofia de <strong>Software Livre e Código Aberto</strong>:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2, lineHeight: 1.8 }}>
              <li><strong>Transparência e Auditabilidade:</strong> O código-fonte integral é público, auditável e depositado em repositório na internet (GitHub), garantindo total lisura em seus algoritmos criptográficos e vedando qualquer apropriação privada de código;</li>
              <li><strong>Licenciamento Livre:</strong> Distribuído sob licenças de código aberto reconhecidas (tais como GPLv3/AGPLv3/MIT) e documentação sob licença Creative Commons (CC BY 4.0);</li>
              <li><strong>Ecossistema de Cooperação:</strong> O sistema segue as diretrizes do Programa Interlegis do Senado Federal, estando disponível para compartilhamento e cessão não onerosa a outros órgãos públicos municipais, estaduais e federais (art. 11 da Resolução).</li>
            </Box>
          </Box>

          {/* 3. Finalidade e Base Legal do Tratamento de Dados (LGPD) */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <PolicyIcon sx={{ color: '#3897F0' }} />
              <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                3. Finalidade e Base Legal do Tratamento (LGPD)
              </Typography>
            </Box>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              O tratamento de dados pessoais no âmbito do ITAPOÁ SIGN fundamenta-se nos <strong>artigos 7º, incisos II e III, e 23 da Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018)</strong>, destinando-se exclusivamente ao <strong>cumprimento de obrigações legais</strong> e à <strong>execução de políticas públicas</strong> pelos órgãos públicos conveniados:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2, lineHeight: 1.8 }}>
              <li>Garantir a autoria, a integridade e o não-repúdio de atos legislativos, administrativos, convênios, contratos e despachos oficiais, em conformidade com as Leis Federais nº 14.063/2020 e nº 14.129/2021;</li>
              <li>Emitir e gerenciar certificados corporativos e assinaturas eletrônicas avançadas e qualificadas (ICP-Brasil);</li>
              <li>Assegurar a economicidade ao erário público, eliminando gastos contínuos com licenças de plataformas privadas de assinatura.</li>
            </Box>
          </Box>

          {/* 4. Dados Pessoais Coletados e Tratados */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600, mb: 1.5 }}>
              4. Dados Pessoais Coletados e Utilizados
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Para garantir a validade formal e a fé pública das assinaturas, são estritamente coletados:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2, lineHeight: 1.8 }}>
              <li><strong>Identificação do Titular:</strong> Nome completo, endereço de e-mail (institucional ou pessoal), CPF, número de telefone celular e cargo/função administrativa;</li>
              <li><strong>Metadados da Assinatura:</strong> Carimbo de data/hora oficial, endereço IP do dispositivo signatário e resumo criptográfico (hash SHA-256) do documento assinado;</li>
              <li><strong>Certificados ICP-Brasil (A1):</strong> Quando o signatário opta pelo uso de certificado pessoal padrão ICP-Brasil, o arquivo digital criptografado (.pfx/.p12) é custodiado sob criptografia moderna AES-256-CBC, utilizado exclusivamente para assinar os documentos determinados pelo próprio titular;</li>
              <li><strong>Notificações em Tempo Real:</strong> Token de dispositivo móvel via Google Firebase Cloud Messaging, empregado com a única finalidade de alertar o signatário sobre novos documentos pendentes de assinatura.</li>
            </Box>
          </Box>

          {/* 5. Dados Biométricos no Aplicativo Móvel */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600, mb: 1.5 }}>
              5. Autenticação Biométrica no Aplicativo Móvel (Touch ID / Face ID)
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              O aplicativo móvel ITAPOÁ SIGN disponibiliza a funcionalidade de acesso por biometria para conveniência e proteção física do aparelho.
            </Typography>
            <Box sx={{ p: 2, borderRadius: 2, backgroundColor: 'rgba(56, 151, 240, 0.12)', border: '1px solid rgba(56, 151, 240, 0.25)', mb: 2 }}>
              <Typography variant="body2" sx={{ color: '#E3F2FD', lineHeight: 1.7 }}>
                <strong>Aviso de Privacidade de Hardware:</strong> A verificação biométrica é executada de forma 100% isolada no processador seguro do aparelho do usuário (Android Keystore / iOS Secure Enclave). <strong>Nenhum dado biométrico, imagem facial ou impressão digital é coletado, transmitido ou armazenado nos servidores da Câmara Municipal de Itapoá.</strong>
              </Typography>
            </Box>
          </Box>

          {/* 6. Infraestrutura Tecnológica e Segurança da Informação */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600, mb: 1.5 }}>
              6. Infraestrutura de Hospedagem e Segurança da Informação
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Em atendimento ao art. 10 da Resolução nº XX/2026, a infraestrutura do sistema é mantida em ambiente controlado de <strong>Servidor Virtual Privado (VPS) e contêineres Docker isolados</strong>, sob as seguintes salvaguardas:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2, lineHeight: 1.8 }}>
              <li>Criptografia de tráfego de ponta a ponta com protocolo HTTPS e certificados TLS (Let's Encrypt);</li>
              <li>Banco de dados seguro (PostgreSQL) com isolamento de rede;</li>
              <li>Autenticação de sessão por tokens JWT (JSON Web Tokens) com ciclo de vida controlado e renovação criptografada;</li>
              <li>Vedação categórica a qualquer comercialização, monetização ou cessão de dados para finalidades publicitárias ou econômicas privadas.</li>
            </Box>
          </Box>

          {/* 7. Validação Pública e Verificação de Autenticidade */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600, mb: 1.5 }}>
              7. Validação Pública e Verificação de Autenticidade
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Em consonância com o art. 45 da Lei Orgânica Municipal de Itapoá e a Resolução nº 14/2016, qualquer cidadão ou autoridade pública poderá conferir a autenticidade e integridade dos documentos assinados eletronicamente por meio do verificador oficial da Casa:
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              🔗 <strong>Portal de Verificação:</strong>{' '}
              <Link href="https://camaraitapoa.sc.gov.br/verificador" target="_blank" rel="noopener noreferrer" sx={{ color: '#3897F0', fontWeight: 600 }}>
                camaraitapoa.sc.gov.br/verificador
              </Link>
            </Typography>
          </Box>

          {/* 8. Direitos do Titular e Exclusão de Dados */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600, mb: 1.5 }}>
              8. Direitos do Titular e Solicitação de Exclusão de Conta
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              O titular dos dados possui o direito inalienável de confirmar a existência de tratamento, consultar seus dados cadastrais, solicitar a retificação de inconsistências e requerer a exclusão de sua conta de usuário.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Para solicitar a <strong>desativação de conta, cancelamento de permissões ou exclusão de cadastro</strong>, o usuário poderá encaminhar requisição formal diretamente ao e-mail institucional <strong>tecnologia@camaraitapoa.sc.gov.br</strong>. Note que os metadados de documentos públicos e atos legislativos pretéritos já concluídos permanecem arquivados por força do dever legal de guarda e preservação da memória documental do Poder Público.
            </Typography>
          </Box>

          {/* 9. Encarregado de Dados (DPO) e Contato Institucional */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600, mb: 1.5 }}>
              9. Contato Institucional e Encarregado de Dados (DPO)
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Nos termos do art. 13 da Resolução nº XX/2026, compete conjuntamente aos responsáveis pelos <strong>Setores de Segurança Digital e de Informática</strong> da Casa coordenar e zelar pelo fiel cumprimento desta política:
            </Typography>
            
            <Box sx={{ p: 3, borderRadius: 3, backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.12)' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#90CAF9', fontWeight: 600 }}>Órgão Controlador:</Typography>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>Câmara Municipal de Itapoá - SC</Typography>

                  <Typography variant="body2" sx={{ color: '#90CAF9', fontWeight: 600 }}>Setor Responsável:</Typography>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>Segurança Digital e Tecnologia da Informação / NILI</Typography>

                  <Typography variant="body2" sx={{ color: '#90CAF9', fontWeight: 600 }}>E-mail do Encarregado (DPO / TI):</Typography>
                  <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 700, color: '#FFFFFF' }}>tecnologia@camaraitapoa.sc.gov.br</Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#90CAF9', fontWeight: 600 }}>Endereço da Sede:</Typography>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>Rua Mariana Michels Borges, nº 1115 - Itapema do Norte, Itapoá - SC, CEP 89249-000</Typography>

                  <Typography variant="body2" sx={{ color: '#90CAF9', fontWeight: 600 }}>Portal Institucional Oficial:</Typography>
                  <Typography variant="body2">
                    <Link href="https://camaraitapoa.sc.gov.br" target="_blank" rel="noopener noreferrer" sx={{ color: '#3897F0', fontWeight: 600 }}>
                      camaraitapoa.sc.gov.br
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;
